import { useState } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import {
    Box,
    Tabs,
    Tab,
    Divider,
    Typography,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { Comments, Log, StatusAvatar, StatusChip } from "../../../shared/components";
import { CheckListDetails } from "../components/CheckListDetails";
import { ApprovalsDetails } from "../components/ApprovalsDetails";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {
    CheckCircle as CheckCircleIcon,
    SyncProblem as SyncProblemIcon,
    Sync as SyncIcon,
    MoreHoriz as MoreHorizIcon
} from "@mui/icons-material";
import { formatDate } from "../../../shared/utils/formatDate";
import { useGetSchedule } from "../../schedulePlanning";

export const StageGateHeader: React.FC<{ id: number; onClose: () => void }> = ({ id, onClose }) => {

    const { data: drawerStatus, isError, isLoading, error } = useGetSchedule(id);

    if(isLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
                my={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if(isError) {
        return (
            <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
                my={2}
                mx={2}
            >
                <Typography variant="subtitle2" color="error">{error?.message || "Something went wrong"}</Typography>
                <IconButton onClick={() => { onClose() }} sx={{ ml: 'auto' }}>
                    <CloseOutlinedIcon sx={{ color: 'action.active' }} />
                </IconButton>
            </Box>
        )
    };

    return (
        <Box
            sx={{
                padding: 2,
                display: "flex",
                justifyContent: "start",
                bgcolor: "action.hover",
                alignItems: "center",
                gap: 2,
                width: '100%',
            }}
        >
            <Box p={1}>
                <StatusAvatar
                    size="large"
                    bgcolor={
                        drawerStatus?.status_display_text === "Completed"
                            ? "success.main"
                            : drawerStatus?.status_display_text ===
                                "Not Started"
                                ? "error.main"
                                : drawerStatus?.status_display_text ===
                                    "In Progress"
                                    ? "warning.main"
                                    : "default"
                    }
                >
                    {drawerStatus?.status_display_text === "Completed" ? (
                        <CheckCircleIcon />
                    ) : drawerStatus?.status_display_text ===
                        "Not Started" ? (
                        <SyncProblemIcon />
                    ) : drawerStatus?.status_display_text ===
                        "In Progress" ? (
                        <SyncIcon />
                    ) : (
                        <MoreHorizIcon />
                    )}
                </StatusAvatar>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                    gap: 0.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="subtitle2" color="text.primary">
                        {drawerStatus?.name}
                    </Typography>
                    <StatusChip
                        color={
                            drawerStatus?.status === "completed"
                                ? "blue"
                                : drawerStatus?.status === "in_progress"
                                    ? "green"
                                    : drawerStatus?.status === "on_hold"
                                        ? "red"
                                        : drawerStatus?.status === "requires_action"
                                            ? "red"
                                            : drawerStatus?.status ===
                                                "awaiting_approval"
                                                ? "amber"
                                                : drawerStatus?.status ===
                                                    "awaiting_decision"
                                                    ? "amber"
                                                    : "default"
                        }
                        label={drawerStatus?.status_display_text || "N/A"}
                    />
                </Box>
                <Typography variant="caption" color="text.primary">
                    {drawerStatus?.description}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Owner: {drawerStatus?.owner}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Due: {drawerStatus?.planned_end_date ? formatDate(drawerStatus?.planned_end_date) : "N/A"}
                    </Typography>
                </Box>
            </Box>
            <IconButton
                onClick={onClose}
                sx={{ ml: "auto" }}
            >
                <CloseOutlinedIcon sx={{ color: "action.active" }} />
            </IconButton>
        </Box>
    )
}

export const StageGateDetails: React.FC<{ id: number; refetch: any; projectId: string | undefined }> = ({ id, refetch, projectId }) => {

    const tabs = [
        { value: "checklist", label: "CHECKLIST" },
        { value: "approvals", label: "APPROVALS" },
        { value: "gate-decision", label: "GATE DECISION" },
        { value: "comments", label: "COMMENTS" },
        { value: "log", label: "LOG" },
    ];

    const [currentTab, setCurrentTab] = useState("checklist");
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>
            <TabContext value={currentTab}>
                <Box display="flex" alignItems="center" alignContent="center">
                    <Tabs
                        value={currentTab}
                        onChange={handleChangeTab}
                        aria-label="Tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: "auto" }}
                    >
                        {tabs.map((t) => (
                            <Tab
                                key={t.value}
                                sx={{ height: "auto", minHeight: "auto" }}
                                label={t.label}
                                value={t.value}
                            />
                        ))}
                    </Tabs>
                </Box>
                <Divider />
                <Box flex={1} overflow="hidden">
                    <TabPanel value="checklist" sx={{ p: 2, height: "100%" }}>
                        <CheckListDetails stageID={String(id || "")} refetch={refetch} />
                    </TabPanel>
                    <TabPanel value="approvals" sx={{ pl: 2, py: 2, pr: 0, height: "100%" }}>
                        <ApprovalsDetails stageID={String(id || "")} gateDecision={false} refetch={refetch} projectId={projectId} />
                    </TabPanel>
                    <TabPanel value="gate-decision" sx={{ pl: 2, py: 2, pr: 0, height: "100%" }}>
                        <ApprovalsDetails stageID={String(id || "")} gateDecision={true} refetch={refetch} projectId={projectId} />
                    </TabPanel>
                    <TabPanel value="comments" sx={{ p: 2, height: "100%" }}>
                        <Comments parent_id={projectId} object_id={String(id || "")} topic={["APPROVAL_COMMENT_ADDED"]} sub_event={["SCHEDULING_AND_PLANNING"]} />
                    </TabPanel>
                    <TabPanel value="log" sx={{ pl: 2, py: 2, pr: 0, height: "100%" }}>
                        <Log parent_id={projectId} object_id={String(id || "")} sub_event={["CHECKLIST", "APPROVAL", "STAGE_GATE_COMMENT"]} topic={["SCHEDULING_AND_PLANNING", "TASK"]} />
                    </TabPanel>
                </Box>
            </TabContext>
        </Box>
    );
};